import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { Group } from "@dashboard/devices/types/index";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { expandGroupIdsWithDescendants, getRootGroupIdsInSelection } from "@dashboard/groups/utils";
import LicenseSelectors from "@dashboard/licenses/store/selectors";
import { Theme, Typography } from "@mui/material";
import { LoadingIcon } from "@shared/Loading";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTable, MinimalTableCell, MinimalTableContainer, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { TableHeaderRows } from "@shared/table/TableHeader";
import TablePagination from "@shared/table/TablePagination";
import { TableRowCells } from "@shared/table/TableRowCells";
import { Tooltip } from "@shared/Tooltip";
import {
    createColumnHelper,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

import TableItem from "./TableItem";

export interface TransformedApp {
    label: string;
    package: string;
    versionCode: number;
    count: number;
}

const useStyles = makeStyles()((muiTheme: Theme) => ({
    container: { height: "100%" },
    table: {
        flex: 1,
        minHeight: 0,
    },
    pagination: {
        marginTop: "auto",
        flexShrink: 0,
        borderTop: `1px solid ${muiTheme.palette.emteria.border.secondary}`,
    },
}));

const getUniqueKey = (app: TransformedApp) => `${app.package}-${app.versionCode}`;

const aggregateInstalledAppsFromGroups = (groups: Group[], baseGroupIds: number[]): TransformedApp[] => {
    const rootGroupIds = getRootGroupIdsInSelection(baseGroupIds, groups);
    const targetGroups = groups.filter((g) => rootGroupIds.includes(g.id));

    const appsMap = new Map<string, TransformedApp>();
    targetGroups.forEach((group) => {
        (group.statistics?.installedApps ?? []).forEach((app) => {
            const key = getUniqueKey(app);
            const existingApp = appsMap.get(key);
            appsMap.set(key, { ...app, count: (existingApp?.count || 0) + app.count });
        });
    });

    return Array.from(appsMap.values());
};

const columnHelper = createColumnHelper<TransformedApp>();

function getColumns() {
    return [
        columnHelper.accessor("label", {
            id: "label",
            header: i18next.t("pages:overviewPage.table.header.application"),
            cell: (info) => (
                <MinimalTableCell>
                    <Typography>{info.row.original.label}</Typography>
                </MinimalTableCell>
            ),
        }),
        columnHelper.accessor("versionCode", {
            id: "versionCode",
            header: i18next.t("pages:overviewPage.table.header.version"),
            cell: (info) => (
                <MinimalTableCell>
                    <Typography>{info.getValue<string>()}</Typography>
                </MinimalTableCell>
            ),
        }),
        columnHelper.accessor("count", {
            id: "count",
            header: i18next.t("pages:overviewPage.table.header.devices"),
            cell: (info) => {
                return <TableItem {...info.row.original} filteredGroupIds={info.table.options.meta?.filteredGroupIds || []} />;
            },
            meta: {
                align: "right",
            },
        }),
    ];
}

type Props = {
    groupIds: number[];
};

export const ManagedAppsCard = ({ groupIds }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);

    const [sorting, setSorting] = useState<SortingState>([
        { id: "count", desc: true },
        { id: "label", desc: false },
    ]);
    const groups = useSelector(selectGroups);
    const baseGroupIds = React.useMemo(() => (!groupIds.length ? groups.map((group) => group.id) : groupIds), [groupIds, groups]);
    const filteredGroupIds = React.useMemo(() => expandGroupIdsWithDescendants(baseGroupIds, groups), [baseGroupIds, groups]);
    const aggregatedApps = React.useMemo(() => aggregateInstalledAppsFromGroups(groups, baseGroupIds), [groups, baseGroupIds]);

    const licenseStatus = useSelector(LicenseSelectors.selectLicensesStatus);
    const isLoading = licenseStatus === "pending";

    const table = useReactTable<TransformedApp>({
        data: aggregatedApps,
        columns: getColumns(),
        initialState: {
            pagination: {
                pageSize: 8,
            },
        },
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        meta: {
            filteredGroupIds,
        },
    });

    const { pageSize, pageIndex } = table.getState().pagination;

    return (
        <SectionPanel disablePadding className={classes.container}>
            <MinimalTableContainer plain className={classes.table}>
                <MinimalTable stickyHeader>
                    <MinimalTableHeader
                        title={{
                            label: t("pages:overviewPage.cards.managedApps.title"),
                            colSpan: table.getVisibleLeafColumns().length,
                            actions: isLoading ? (
                                <Tooltip title={t("pages:overviewPage.tooltips.loadingMoreData")}>
                                    <span>
                                        <LoadingIcon />
                                    </span>
                                </Tooltip>
                            ) : null,
                        }}
                    >
                        <TableHeaderRows headerGroups={table.getHeaderGroups()} />
                    </MinimalTableHeader>
                    <TableBody table={table} noDataMessage={t("pages:overviewPage.cards.managedApps.noApplications")}>
                        {table.getRowModel().rows.map((row) => (
                            <HoverRow key={row.id} {...row} clickable={false}>
                                <TableRowCells row={row} />
                            </HoverRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </MinimalTableContainer>
            <TablePagination
                component="div"
                className={classes.pagination}
                rowsPerPageOptions={[8, 24, 48]}
                count={table.getFilteredRowModel().rows.length}
                rowsPerPage={pageSize}
                page={pageIndex}
                onPageChange={(_event, page) => table.setPageIndex(page)}
                onRowsPerPageChange={(e) => {
                    const size = e.target.value ? Number(e.target.value) : 8;
                    table.setPageSize(size);
                }}
                slotProps={{
                    actions: {
                        nextButton: { title: "" },
                        previousButton: { title: "" },
                    },
                }}
            />
        </SectionPanel>
    );
};
